import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, MailPlus, Inbox } from "lucide-react";
import Messages from "@/components/Messages";
import FarmProfileForm from "@/components/FarmProfileForm";
import OnboardingTracker from "@/components/OnboardingTracker";
import MemberLedger from "@/components/MemberLedger";
import ImageUploader from "@/components/ImageUploader";
import { Link } from "react-router-dom";
import { CATEGORY_IDS, CATEGORY_LABEL } from "@/lib/catalog";

const CATEGORIES = CATEGORY_IDS;

export default function FarmerDashboard() {
  const { user, refresh } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [requests, setRequests] = useState([]);
  const [states, setStates] = useState([]);

  const [openAdd, setOpenAdd] = useState(false);
  const [openNews, setOpenNews] = useState(false);
  const [openVerify, setOpenVerify] = useState(false);
  const [verifyFile, setVerifyFile] = useState(null);
  const [verifyUploading, setVerifyUploading] = useState(false);

  const [form, setForm] = useState({
    title: "", description: "", category: "vegetables", price: 0,
    listing_type: "sale", availability: "ready", state: user?.state || "", pickup_city: "", image_url: "", barter_for: "",
  });
  const [news, setNews] = useState({ title: "", content: "" });
  const [verifyUrl, setVerifyUrl] = useState("");

  const load = async () => {
    const [p, o, n, r, s] = await Promise.all([
      api.get("/products", { params: { farmer_id: user.user_id } }),
      api.get("/orders"),
      api.get("/newsletters", { params: { farmer_id: user.user_id } }),
      api.get("/driver-requests"),
      api.get("/states"),
    ]);
    setProducts(p.data); setOrders(o.data); setNewsletters(n.data); setRequests(r.data); setStates(s.data);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const addProduct = async () => {
    try {
      await api.post("/products", { ...form, price: parseFloat(form.price) || 0 });
      toast.success("Product listed.");
      setOpenAdd(false);
      setForm({ ...form, title: "", description: "", price: 0, image_url: "", barter_for: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add product.");
    }
  };

  const removeProduct = async (id) => {
    await api.delete(`/products/${id}`);
    toast.success("Removed.");
    load();
  };

  const postNewsletter = async () => {
    try {
      await api.post("/newsletters", news);
      toast.success("Newsletter published.");
      setOpenNews(false);
      setNews({ title: "", content: "" });
      load();
    } catch {
      toast.error("Could not publish.");
    }
  };

  const submitVerification = async () => {
    if (!verifyFile) {
      toast.error("Please choose a file.");
      return;
    }
    setVerifyUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", verifyFile);
      await api.post("/uploads/verification", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Verification uploaded. Your farm is now verified.");
      setOpenVerify(false);
      setVerifyFile(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setVerifyUploading(false);
    }
  };

  const totalRevenue = orders.reduce((s, o) => s + (o.status === "delivered" ? o.quantity * 0 : 0), 0); // placeholder
  const stat = (label, value, testid) => (
    <div data-testid={testid} className="bg-white border border-stone-200 p-6 rounded-md">
      <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">{label}</div>
      <div className="mt-3 font-serif text-3xl text-stone-900">{value}</div>
    </div>
  );

  return (
    <div className="space-y-8">
      <OnboardingTracker />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {stat("Listings", products.length, "stat-listings")}
        {stat("Orders", orders.length, "stat-orders")}
        {stat("Newsletters", newsletters.length, "stat-news")}
        {stat("Food Requests", requests.length, "stat-requests")}
      </div>

      {!user.farm_name && (
        <div className="bg-emerald-800 text-stone-50 rounded-md p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <div className="font-serif text-2xl">Complete your hobby farm profile</div>
            <p className="text-sm text-stone-100/90 font-sans mt-1 max-w-xl">Add your farm name, address, phone and website so clients can find you in the directory. Profiles without these details aren't shown publicly.</p>
          </div>
          <a href="#farm-profile" className="bg-amber-700 hover:bg-amber-600 text-stone-50 px-5 py-3 rounded-md font-sans text-sm">Complete now</a>
        </div>
      )}

      {!user.verified && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <div className="font-serif text-xl text-stone-900">Verify your hobby farm</div>
            <p className="text-sm text-stone-600 font-sans mt-1">Upload identification to unlock the verified badge.</p>
          </div>
          <Dialog open={openVerify} onOpenChange={setOpenVerify}>
            <DialogTrigger asChild>
              <Button data-testid="verify-btn" className="bg-amber-700 hover:bg-amber-600 text-stone-50"><ShieldCheck size={16} className="mr-2" />Verify Hobby Farm</Button>
            </DialogTrigger>
            <DialogContent className="bg-stone-50">
              <DialogHeader><DialogTitle className="font-serif text-2xl">Upload ID</DialogTitle></DialogHeader>
              <input
                data-testid="verify-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={(e) => setVerifyFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-stone-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-emerald-800 file:text-stone-50 hover:file:bg-emerald-700"
              />
              <p className="text-xs text-stone-500">JPG, PNG, WEBP, or PDF · max 8 MB. Only you and Azure Gypsy admins can view this file.</p>
              <DialogFooter>
                <Button data-testid="verify-submit" disabled={verifyUploading} onClick={submitVerification} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">
                  {verifyUploading ? "Uploading..." : "Upload & Verify"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <Tabs defaultValue="inventory">
        <div className="overflow-x-auto -mx-2 px-2">
          <TabsList className="bg-stone-100 flex-nowrap whitespace-nowrap w-max sm:w-auto">
            <TabsTrigger data-testid="tab-inventory" value="inventory">Inventory</TabsTrigger>
            <TabsTrigger data-testid="tab-profile" value="profile">Farm Profile</TabsTrigger>
            <TabsTrigger data-testid="tab-orders" value="orders">Orders</TabsTrigger>
            <TabsTrigger data-testid="tab-deposits" value="deposits">Deposits</TabsTrigger>
            <TabsTrigger data-testid="tab-newsletters" value="newsletters">Newsletters</TabsTrigger>
            <TabsTrigger data-testid="tab-driver-requests" value="requests">Driver Requests</TabsTrigger>
            <TabsTrigger data-testid="tab-messages" value="messages">Messages</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="inventory" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={openAdd} onOpenChange={setOpenAdd}>
              <DialogTrigger asChild>
                <Button data-testid="add-product-btn" className="bg-emerald-800 hover:bg-emerald-700 text-stone-50"><Plus size={16} className="mr-2" />Add Product</Button>
              </DialogTrigger>
              <DialogContent className="bg-stone-50 max-w-2xl">
                <DialogHeader><DialogTitle className="font-serif text-2xl">List a New Product</DialogTitle></DialogHeader>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input data-testid="add-title" placeholder="Title (e.g., Heirloom Tomatoes)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white" />
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="add-category" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c] || c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={form.listing_type} onValueChange={(v) => setForm({ ...form, listing_type: v })}>
                    <SelectTrigger data-testid="add-listing-type" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sale">For Sale</SelectItem>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="barter">Barter</SelectItem>
                      <SelectItem value="wholesale">Wholesale</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={form.availability} onValueChange={(v) => setForm({ ...form, availability: v })}>
                    <SelectTrigger data-testid="add-availability" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ready">Ready Now (direct from crops)</SelectItem>
                      <SelectItem value="wait">Wait for Harvest</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input data-testid="add-price" type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-white" />
                  <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                    <SelectTrigger data-testid="add-state" className="bg-white"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid="add-pickup-city" placeholder="Pickup City" value={form.pickup_city} onChange={(e) => setForm({ ...form, pickup_city: e.target.value })} className="bg-white" />
                  {form.listing_type === "barter" && (
                    <Input data-testid="add-barter" placeholder="Looking to barter for..." value={form.barter_for} onChange={(e) => setForm({ ...form, barter_for: e.target.value })} className="bg-white sm:col-span-2" />
                  )}
                  <Textarea data-testid="add-description" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white sm:col-span-2" rows={3} />
                  <div className="sm:col-span-2">
                    <ImageUploader
                      value={form.image_url}
                      onChange={(v) => setForm({ ...form, image_url: v })}
                      label="Product photo"
                      testid="add-product-photo"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="add-product-submit" onClick={addProduct} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">List Product</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {products.length === 0 ? (
            <div className="bg-white border border-stone-200 p-10 text-center rounded-md font-sans text-stone-500">No listings yet. Add your first product above.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((p) => (
                <div key={p.product_id} className="bg-white border border-stone-200 rounded-md p-5">
                  <div className="flex justify-between gap-3">
                    <div>
                      <div className="font-serif text-lg text-stone-900">{p.title}</div>
                      <div className="text-xs text-stone-500 mt-1 uppercase tracking-[0.15em]">{p.category} · {p.listing_type}</div>
                    </div>
                    <button data-testid={`delete-product-${p.product_id}`} onClick={() => removeProduct(p.product_id)} className="text-stone-400 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                  <p className="mt-3 text-sm text-stone-600 font-sans line-clamp-3">{p.description}</p>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-stone-500 font-sans">{p.pickup_city}, {p.state}</span>
                    <span className="font-serif text-emerald-800">${p.price.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 ? (
            <div className="bg-white border border-stone-200 p-10 text-center rounded-md font-sans text-stone-500">No orders yet.</div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 text-left text-stone-600 uppercase tracking-[0.15em] text-xs">
                  <tr>
                    <th className="p-4">Product</th><th className="p-4">Client</th><th className="p-4">Status</th><th className="p-4">Tip</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_id} className="border-t border-stone-100">
                      <td className="p-4 font-sans">{o.product_title}</td>
                      <td className="p-4 font-sans text-stone-600">{o.client_name}</td>
                      <td className="p-4"><Badge className="bg-stone-800 text-stone-50">{o.status}</Badge></td>
                      <td className="p-4 font-sans text-stone-700">${o.tip?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="deposits" className="mt-6">
          <MemberLedger />
        </TabsContent>

        <TabsContent value="newsletters" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={openNews} onOpenChange={setOpenNews}>
              <DialogTrigger asChild>
                <Button data-testid="new-newsletter-btn" className="bg-emerald-800 hover:bg-emerald-700 text-stone-50"><MailPlus size={16} className="mr-2" />New Release</Button>
              </DialogTrigger>
              <DialogContent className="bg-stone-50">
                <DialogHeader><DialogTitle className="font-serif text-2xl">New Release</DialogTitle></DialogHeader>
                <Input data-testid="news-title" placeholder="Title" value={news.title} onChange={(e) => setNews({ ...news, title: e.target.value })} className="bg-white" />
                <Textarea data-testid="news-content" rows={5} placeholder="What's new on the farm?" value={news.content} onChange={(e) => setNews({ ...news, content: e.target.value })} className="bg-white" />
                <DialogFooter><Button data-testid="news-submit" onClick={postNewsletter} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">Publish</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {newsletters.length === 0 ? (
            <div className="bg-white border border-stone-200 p-10 text-center rounded-md font-sans text-stone-500">No newsletters yet.</div>
          ) : (
            <div className="space-y-4">
              {newsletters.map((n) => (
                <div key={n.id} className="bg-white border border-stone-200 p-6 rounded-md">
                  <div className="font-serif text-2xl text-stone-900">{n.title}</div>
                  <p className="mt-2 text-sm text-stone-600 font-sans whitespace-pre-line">{n.content}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="mt-6">
          {requests.length === 0 ? (
            <div className="bg-white border border-stone-200 p-10 text-center rounded-md font-sans text-stone-500 flex flex-col items-center gap-3">
              <Inbox size={28} className="text-stone-400" />
              No driver food requests yet.
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <div key={r.id} className="bg-white border border-stone-200 p-5 rounded-md">
                  <div className="flex items-center justify-between">
                    <div className="font-serif text-lg text-stone-900">{r.driver_name}</div>
                    <Badge className="bg-amber-700 text-stone-50">{r.status}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-stone-600 font-sans">{r.message}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <Messages />
        </TabsContent>

        <TabsContent value="profile" className="mt-6" id="farm-profile">
          <FarmProfileForm />
        </TabsContent>
      </Tabs>
    </div>
  );
}